import { useState } from 'react';
import { Drawer, Text, UnstyledButton } from '@mantine/core';
import { SCENARIO_ROWS } from '../../lib/chat';

/**
 * "Text chat — pick a scenario" (design `12e`).
 *
 * **A sheet, not a screen.** `12e` is drawn as a bare card rather than in a device frame, and the
 * app already has a sheet vocabulary — `GlossSheet`, `PatternSheet`, `VocabSheet` — so this is a
 * Mantine `Drawer` on `position="bottom"`. A screen would have meant a route, a back stack entry
 * and a second place to arrive at `/chat` from, all for one decision that takes one tap.
 *
 * Three rows plus the free-text row. The free-text row becomes an input **in place**, which is
 * what `12e`'s dashed border and "or describe your own…" draw: a second sheet for it would put
 * two dismissals between the learner and a conversation.
 *
 * Spoken dialogue appears here only with `capabilities.spokenDialogue` — **absent, not dimmed**,
 * exactly as the Practice tile already does it. Today that capability is false for everyone
 * (`language_kit.stt` is still `pending`), so the row does not render; M6d is what turns it on,
 * and the screen behind it is M6d's too.
 */

export interface ScenarioSheetProps {
  opened: boolean;
  onClose: () => void;
  /** a scenario id from `SCENARIO_ROWS`, or the learner's own words */
  onPick: (scenario: string) => void;
  /** `conversationStart` is `entitled`: false → the rows are inert and one calm line says why */
  entitled?: boolean;
  /** a conversation is being opened — the rows stop taking taps so two are never started */
  busy?: boolean;
  /** `Me.capabilities[target].spokenDialogue` */
  spokenDialogue?: boolean;
  onSpoken?: () => void;
  /** a refusal from `conversationStart`, shown here rather than on the screen behind the sheet */
  calm?: string | null;
}

export function ScenarioSheet({ opened, onClose, ...rest }: ScenarioSheetProps) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="auto"
      withCloseButton={false}
      trapFocus={false}
      lockScroll={false}
      overlayProps={{ backgroundOpacity: 0.42, blur: 0 }}
      styles={{
        content: {
          background: 'var(--nn-bg)',
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          borderTop: '1px solid var(--nn-hairline)',
        },
        body: { padding: '14px 16px calc(18px + env(safe-area-inset-bottom))' },
      }}
    >
      <ScenarioPicker {...rest} />
    </Drawer>
  );
}

/**
 * The sheet's contents, separated from the sheet.
 *
 * Not tidiness: Mantine's `Drawer` renders through a `Portal`, which produces **nothing** under
 * `renderToStaticMarkup` — measured, 860 characters of Mantine's own stylesheet and no rows — so
 * every rule this component has to obey (the design's three rows, the free-text row, "spoken
 * dialogue is absent, not dimmed", the unentitled line) would be untestable in a node suite if it
 * lived inside the Drawer. The screen renders `ScenarioSheet`; the test renders this.
 */
export function ScenarioPicker({
  onPick,
  entitled = true,
  busy = false,
  spokenDialogue = false,
  onSpoken,
  calm = null,
}: Omit<ScenarioSheetProps, 'opened' | 'onClose'>) {
  const [own, setOwn] = useState<string | null>(null);
  const pickable = entitled && !busy;

  return (
    <>
      <div style={{ fontSize: 14, color: 'var(--nn-text-strong)' }}>
        Text chat — pick a scenario
      </div>

      <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {SCENARIO_ROWS.map((s) => (
          <UnstyledButton
            key={s.id}
            disabled={!pickable}
            onClick={() => onPick(s.id)}
            style={{ ...row, opacity: pickable ? 1 : 0.5 }}
          >
            {s.label}
          </UnstyledButton>
        ))}

        {/* The design's dashed row. Tapping it swaps it for the input, and the input keeps the
            same box so nothing under the sheet moves. */}
        {own === null ? (
          <UnstyledButton
            disabled={!pickable}
            onClick={() => setOwn('')}
            style={{
              ...row,
              background: 'color-mix(in srgb, var(--nn-text) 3%, transparent)',
              border: '1px dashed color-mix(in srgb, var(--nn-text) 14%, transparent)',
              color: 'var(--nn-muted)',
              opacity: pickable ? 1 : 0.5,
            }}
          >
            or describe your own…
          </UnstyledButton>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="nn-chat-own"
              autoFocus
              value={own}
              maxLength={200}
              placeholder="at the barber, arguing about football…"
              onChange={(e) => setOwn(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && own.trim() && pickable) onPick(own.trim());
              }}
            />
            <UnstyledButton
              disabled={!pickable || own.trim().length === 0}
              onClick={() => onPick(own.trim())}
              style={{
                ...startButton,
                opacity: pickable && own.trim().length > 0 ? 1 : 0.45,
              }}
            >
              Start
            </UnstyledButton>
          </div>
        )}

        {/* Absent, not dimmed. */}
        {spokenDialogue && onSpoken && (
          <UnstyledButton disabled={!pickable} onClick={onSpoken} style={row}>
            spoken dialogue →
          </UnstyledButton>
        )}
      </div>

      {!entitled && (
        <Text fz={11.5} c="var(--nn-muted)" mt={10}>
          {/* The same shape the journal's list uses: what is gated, and what is not. */}
          Chat is part of the paid layer — the conversations you have already had are still here.
        </Text>
      )}
      {calm && (
        <Text fz={11.5} c="var(--nn-muted)" mt={10} role="status">
          {calm}
        </Text>
      )}
    </>
  );
}

const row: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'start',
  background: 'var(--nn-panel)',
  border: '1px solid var(--nn-hairline)',
  borderRadius: 9,
  padding: '10px 13px',
  fontSize: 13,
  color: 'var(--nn-text-strong)',
};

const startButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '9px 14px',
  borderRadius: 8,
  background: 'var(--nn-accent)',
  color: 'var(--nn-on-accent)',
  fontSize: 12.5,
  fontWeight: 600,
  flexShrink: 0,
};
